import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface PageHeaderProps {
  /** Page title (h1). Omit when the title lives in the top bar via useAdminPageTitle. */
  title?: ReactNode;
  /** Small uppercase line above the title. */
  eyebrow?: ReactNode;
  /** Supporting copy under the title. */
  description?: ReactNode;
  /** Right-aligned actions (primary button last). */
  actions?: ReactNode;
  /** Optional content above the title row (e.g. a back link). */
  before?: ReactNode;
  className?: string;
}

/** Page title row: eyebrow, h1, description and actions — wraps on small screens. */
export function PageHeader({ title, eyebrow, description, actions, before, className }: PageHeaderProps) {
  return (
    <div className={cn('flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between', className)}>
      <div className="min-w-0">
        {before}
        {eyebrow && <p className="adm-label mb-1.5">{eyebrow}</p>}
        {title && (
          <h1 className="text-[22px] font-semibold leading-tight tracking-tight text-foreground md:text-[24px]">
            {title}
          </h1>
        )}
        {description && <p className="mt-1 text-[13.5px] text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
